<template>
	<div>
		<app-app-header v-model="indexStore.isHeaderVisible"
						:title="$t('view.search.title')"
						main hide-on-scroll/>

		<app-content>
			<app-header-toolbar :on-top="!indexStore.isHeaderVisible" flat-on-top-mobile>
				<form-input v-model="searchQuery"
							:icon="icons.search"
							type="search"
							enterkeyhint="search"
							ref="searchInputElement"
							@focus="handleSearchInputFocus"
							@blur="handleSearchInputBlur"
							name="search" :placeholder="$t('placeholder.search_anything')"
							inverted :autofocus="$device.isDesktop" hide-details clearable/>

				<template #tools>
					<chip v-model="withEvents" small class="mr-8" :color="withEvents ? 'primary' : null" clickable>{{ $t('label.events') }}</chip>
					<chip v-model="withClubs" small class="mr-8" :color="withClubs ? 'primary' : null" clickable>{{ $t('label.clubs') }}</chip>
					<chip v-model="withAthletes" small class="mr-8" :color="withAthletes ? 'primary' : null" clickable>{{ $t('label.athletes') }}</chip>
				</template>

			</app-header-toolbar>

			<page-content desktop-background xs-full sm-full>
				<container xs-full sm-full>
					<card :flat="!$device.isDesktop">
						<data-loader v-if="searchQuery" url="/v1/search"
									 :params="params"
									 as-guest
									 v-model:data="results"
									 :filter="filterData" :persist-filter-keys="['search', 'events', 'clubs', 'athletes']"
									 on-scroll @filtering="handleApplyingFilter">

							<template #loading>
								<div v-for="modelLoaderItem in params.limit" class="divided w-full" :key="'model-loader-' + modelLoaderItem">
									<profile-preview-skeleton no-spacing flat class="lg:px-24"/>
								</div>
							</template>

							<template v-slot="{data}">
								<div v-for="(model, modelIndex) in data" :key="modelIndex" class="divided">
									<profile-preview :model="model"
													 list-name="Global Search" :position="modelIndex + 1" flat no-spacing
													 :class="{'bg-gray-150': modelIndex === arrowSelectIndex}"
													 class="lg:px-24" @click="handleSelectedModel(model)"/>
								</div>
							</template>

							<template #missingsearch>
								<missing-info :text="$t('missing.search')" xs-tile no-spacing/>
							</template>

						</data-loader>
						<template v-else-if="lastSearchedModels !== null">
							<template v-if="lastSearchedModels.length">
								<card-title spaced-content>
									<div class="w-full flex justify-between items-center">
										<span>{{ $t('label.last_searched') }}</span>
										<span class="text-xs font-regular font-text text-gray-400 cursor-pointer" @click="removeLastSearchedModels">{{ $t('action.remove') }}</span>
									</div>
								</card-title>
								<div v-for="(model, modelIndex) in lastSearchedModels" :key="modelIndex" class="divided">
									<!-- We cannot benefit from cache busters on stored search models as we do not know if the models were updated -->
									<profile-preview :model="model"
													 :to="model.model_name === 'user' ? $app('athletes-user', {params: {user: model._key}}) : $app(`${model.model_name}s-${model.model_name}`, {params: {[model.model_name]: model._key}})"
													 list-name="Global Search" :position="modelIndex + 1" flat no-spacing
													 :class="{'bg-gray-150': modelIndex === arrowSelectIndex}"
													 class="lg:px-24"/>
								</div>
							</template>
							<div v-else @click="focusSearch">
								<alert :content="$t('text.discover_spoferan')" no-spacing class="min-h-240"/>
							</div>
						</template>
					</card>
				</container>
			</page-content>
		</app-content>
	</div>
</template>

<script setup lang="ts">

import {debounce, scrollToPosition} from "@spoferan/spoferan-ts-core";
import type {MainModelPreview} from "@spoferan/spoferan-ts-core";
import {useIndexStore} from "../store";
import type {Ref} from "vue";
import {searchIcon} from "@spoferan/nuxt-spoferan/icons";

const indexStore = useIndexStore();
const {t} = useI18n();
const {$web, $analytics} = useNuxtApp();
const route = useRoute();

const lastSearchedModelsStorageKey = 'searched';
const lastSearchedModelsSchemaVersion = 1; // Storing a version for the schema of the searched models enables us to invalidate the results when the schema changes

const {
	handleSearchInputFocus,
	handleSearchInputBlur
} = useSearchPage();

const results: Ref<MainModelPreview[]> = ref([]);
const searchQuery = ref(route.query.search);
const withEvents = ref(route.query.events === 'true');
const withClubs = ref(route.query.clubs === 'true');
const withAthletes = ref(route.query.athletes === 'true');

const searchInputElement = ref(null);

const lastSearchedModels: Ref<{}[]|null> = ref(null);

// The index of the search result item that is currently selected by the keyboard arrows
const arrowSelectIndex = ref(-1);

const params = {
	limit: 30
}

const icons = {
	search: searchIcon
}

const filterData = computed(() => {
	return {
		search: searchQuery.value,
		events: withEvents.value ? true : undefined,
		athletes: withAthletes.value ? true : undefined,
		clubs: withClubs.value ? true : undefined,
	};
});

watch(searchQuery, (newValue) => {
	trackSearchQuery(newValue);
});

function handleApplyingFilter() {
	arrowSelectIndex.value = -1;
	nextTick().then(() => {
		scrollToPosition(0); // Easier to scroll to very top to support good UX for desktop and mobile
	});
}

// Lets the user search for options and control the select while typing on the keyboard
function enableControls() {
	document.addEventListener('keydown', preventKeydownEvents);
	document.addEventListener('keyup', handleControlEvent);
}

// Prevents the user from searching and controlling the select while typing on the keyboard
function disableControls() {
	document.removeEventListener('keydown', preventKeydownEvents);
	document.removeEventListener('keyup', handleControlEvent);
}

function preventKeydownEvents(event) {
	// Prevent window scrolling on arrows
	if ([37, 38, 39, 40].indexOf(event.keyCode) > -1) {
		event.preventDefault();
	}
}

function handleControlEvent(event) {
	if (!event || !event.key) {
		return;
	}

	const key = event.key.toLowerCase();

	switch (key) {
		case 'enter':
			if (arrowSelectIndex.value > -1 && results.value.length > arrowSelectIndex.value) {
				const model = results.value[arrowSelectIndex.value];
				if (model) {
					navigateTo($web(model.model_name + 's.show.index', model._key));
				}
			}

			break;
		case 'arrowdown':
			arrowSelectIndex.value = arrowSelectIndex.value === (results.value.length - 1) ? 0 : arrowSelectIndex.value + 1;

			break;
		case 'arrowup':
			arrowSelectIndex.value = arrowSelectIndex.value === 0 ? (results.value.length - 1) : (arrowSelectIndex.value - 1);
			break;
	}
}

function handleSelectedModel(model: MainModelPreview) {
	const mutatingLastSearchedModels: MainModelPreview[] = [...lastSearchedModels.value];

	// If the model is already included, we want to remove it and insert it at the first position
	const existingIndex = mutatingLastSearchedModels.findIndex(existingModel => existingModel.model_name === model.model_name && existingModel.id === model.id)
	if (existingIndex > -1) {
		mutatingLastSearchedModels.splice(existingIndex, 1);
	}

	// Insert the newly searched model at the first position of the search list
	mutatingLastSearchedModels.splice(0, 0, model);

	// We want to keep a max of 10 models in the search history
	mutatingLastSearchedModels.splice(10);

	localStorage.setItem(lastSearchedModelsStorageKey, JSON.stringify({
		version: lastSearchedModelsSchemaVersion,
		models: mutatingLastSearchedModels
	}));
}

function initLastSearchedModels() {
	const lastSearchValue = JSON.parse(localStorage.getItem(lastSearchedModelsStorageKey) ?? '{}');

	// We only restore the last searched models when the schema version matches
	if (lastSearchValue.version === lastSearchedModelsSchemaVersion) {
		lastSearchedModels.value = lastSearchValue.models ?? [];
	} else {
		removeLastSearchedModels();
	}
}

function removeLastSearchedModels() {
	lastSearchedModels.value = [];
	localStorage.removeItem(lastSearchedModelsStorageKey);
}

function focusSearch() {
	searchInputElement.value?.focus()
}

const trackSearchQuery = debounce(function (query) {
	if (query && query.length >= 3) {
		$analytics.trackEvent('search', {search_term: query});
	}
}, 1000);

onMounted(() => {
	enableControls();
	initLastSearchedModels();
});

onBeforeUnmount(() => {
	disableControls();
})

useMeta({
	title: t('view.search.title'),
	description: t('view.search.description'),
	canonicalQueries: Object.keys(filterData.value)
});

useSchemaOrg([
	defineWebPage({
		'@type': ['CollectionPage', 'SearchResultsPage']
	}),
])

</script>

